import { Box, Typography, IconButton, Tooltip, Button } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import { formatProduct } from '../utils/utilfunction';
import { formatTimeToIST } from '../utils/dateUtils';

// Status color configuration
const statusColorConfig = {
  PENDING: 'warning.main',
  COMPLETE: 'success.main',
  REJECTED: 'error.main',
  CONFIRMED: 'success.main',
  CANCELLED: 'error.main',
  EXCHANGE_RESPONSE_PENDING: 'warning.main',
  CANCEL_CONFIRMED: 'error.main',
  default: 'text.secondary'
};

const getStatusColor = (status) => statusColorConfig[status] || statusColorConfig.default;

const createSymbolCell = (item) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <Typography
      variant="body2"
      sx={{
        fontWeight: 'medium',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}
    >
      {item.trading_symbol}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      ({item.exchange === 'NFO' ? 'Derivatives' : item.exchange === 'MCX' ? 'Commodities' : item.exchange==='NSE' ? 'Indices' : item.exchange})
    </Typography>
  </Box>
);

const createSideCell = (item, theme) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
    <Typography
      sx={{
        color: item.order_side === 'BUY' ? 'success.main' : 'error.main',
        fontWeight: 'bold'
      }}
    >
      {item.order_side}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {formatProduct(item.product)}
    </Typography>
  </Box>
);

export const pendingOrdersConfig = (handleModifyOrder, handleDeleteClick, isMobile) => [
  {
    field: 'symbol',
    headerName: 'Symbol',
    width: isMobile ? '25%' : '20%',
    render: createSymbolCell
  },
  {
    field: 'side',
    headerName: 'Side',
    width: isMobile ? '18%' : '15%',
    render: createSideCell
  },
  {
    field: 'order_type',
    headerName: 'Type',
    width: isMobile ? '12%' : '12%',
    align: 'right',
    render: (item) => `${item.order_type}`
  },
  {
    field: 'quantity',
    headerName: 'Qty',
    width: isMobile ? '12%' : '12%',
    align: 'right',
    render: (item) => `${item.filled_quantity}/${item.quantity}`
  },
  {
    field: 'price',
    headerName: 'Price',
    width: isMobile ? '15%' : '13%',
    align: 'right',
    render: (item) => `₹${item.price.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`
  },
  {
    field: 'order_entry_time',
    headerName: 'Time',
    width: '12%',
    hideOnMobile: true,
    format: (value) => formatTimeToIST(value)
  },
  {
    field: 'order_status',
    headerName: 'Status',
    width: isMobile ? '15%' : '15%',
    render: (item) => (
      <Box sx={{ 
        color: getStatusColor(item.order_status),
        fontWeight: 'medium',
        fontSize: isMobile ? '0.65rem' : '0.75rem',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}>
        {item.order_status}
      </Box>
    )
  },
  {
    field: 'actions',
    headerName: 'Actions',
    width: isMobile ? '15%' : '13%',
    render: (item) => (
      <Box sx={{ display: 'flex', gap: isMobile ? 1 : 1.5, justifyContent: 'flex-start' }}>
        <Tooltip title="Modify Order" arrow>
          <IconButton
            size="small"
            onClick={(e) => handleModifyOrder(item, e)}
            disabled={item.order_status === "EXCHANGE_RESPONSE_PENDING"}
            sx={{
              p: isMobile ? "4px" : "6px",
              color: "primary.main",
              "&:hover": {
                backgroundColor: "rgba(25, 118, 210, 0.04)",
              },
            }}
          >
            <EditIcon sx={{ fontSize: isMobile ? "1.1rem" : "1.3rem" }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Cancel Order" arrow>
          <IconButton
            size="small"
            onClick={(e) => handleDeleteClick(item, e)}
            disabled={item.order_status === "EXCHANGE_RESPONSE_PENDING"}
            sx={{
              p: isMobile ? "4px" : "6px",
              color: "error.main",
              "&:hover": {
                backgroundColor: "rgba(211, 47, 47, 0.04)",
              },
            }}
          >
            <CancelIcon sx={{ fontSize: isMobile ? "1.1rem" : "1.3rem" }} />
          </IconButton>
        </Tooltip>
      </Box>
    )
  }
];

export const completedOrdersConfig = (isMobile) => [
  {
    field: 'symbol',
    headerName: 'Symbol',
    width: isMobile ? '25%' : '20%',
    render: createSymbolCell
  },
  {
    field: 'side',
    headerName: 'Side',
    width: isMobile ? '18%' : '15%',
    render: createSideCell
  },
  {
    field: 'order_type',
    headerName: 'Type',
    width: isMobile ? '12%' : '12%',
    align: 'right',
    render: (item) => `${item.order_type}`
  },
  {
    field: 'quantity',
    headerName: 'Qty',
    width: isMobile ? '12%' : '12%',
    align: 'right',
    render: (item) => `${item.filled_quantity}/${item.quantity}`
  },
  {
    field: 'price',
    headerName: 'Price',
    width: isMobile ? '15%' : '13%',
    align: 'right',
    render: (item) => `₹${item.price.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`
  },
  {
    field: 'order_status',
    headerName: 'Status',
    width: isMobile ? '15%' : '15%',
    render: (item) => (
      <Box sx={{ 
        color: getStatusColor(item.order_status),
        fontWeight: 'medium',
        fontSize: isMobile ? '0.65rem' : '0.75rem',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}>
        {item.order_status}
      </Box>
    )
  },
  {
    field: 'order_entry_time',
    headerName: 'Time',
    width: '12%',
    hideOnMobile: true,
    format: (value) => formatTimeToIST(value)
  },
];

export const tradesConfig = (isMobile) => [
  {
    field: 'symbol',
    headerName: 'Symbol',
    width: isMobile ? '30%' : '25%',
    render: createSymbolCell
  },
  {
    field: 'side',
    headerName: 'Side',
    width: isMobile ? '20%' : '15%',
    render: createSideCell
  },
  {
    field: 'trade_quantity',
    headerName: 'Qty',
    width: isMobile ? '25%' : '15%',
    align: 'right'
  },
  {
    field: 'trade_price',
    headerName: 'Price',
    width: isMobile ? '25%' : '15%',
    align: 'right',
    render: (item) => `₹${item.trade_price.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`
  },
  {
    field: 'order_entry_time',
    headerName: 'Time',
    width: '12%',
    hideOnMobile: true,
    format: (value) => formatTimeToIST(value)
  },
  // {
  //   field: 'trade_status',
  //   headerName: 'Status',
  //   width: isMobile ? '15%' : '15%',
  //   render: (item) => (
  //     <Box sx={{ 
  //       color: getStatusColor(item.trade_status),
  //       fontWeight: 'medium',
  //       fontSize: isMobile ? '0.65rem' : '0.75rem',
  //       whiteSpace: 'nowrap',
  //       overflow: 'hidden',
  //       textOverflow: 'ellipsis'
  //     }}>
  //       {item.trade_status}
  //     </Box>
  //   )
  // }
];

// Position quantity and price color config
const quantityColorConfig = {
  positive: 'success.main',
  negative: 'error.main',
  neutral: 'text.primary'
};

const getQuantityColor = (value) => {
  if (value > 0) return quantityColorConfig.positive;
  if (value < 0) return quantityColorConfig.negative;
  return quantityColorConfig.neutral;
};

export const positionsConfig = (isMobile, exchangeSpecificStringifiedNumber) => [
  {
    field: 'symbol',
    headerName: 'Symbol',
    width: '200px',
    render: createSymbolCell
  },
  {
    field: 'product',
    headerName: 'Product',
    width: '100px',
    format: formatProduct
  },
  {
    field: 'net_quantity',
    headerName: 'Qty',
    width: '80px',
    align: 'right',
    render: (item) => (
      <Box sx={{ 
        color: getQuantityColor(item.net_quantity),
        fontWeight: 500
      }}>
        {item.net_quantity}
      </Box>
    )
  },
  {
    field: 'buyAverage',
    headerName: 'Buy Avg',
    width: '100px',
    align: 'right',
    render: (item) => exchangeSpecificStringifiedNumber(item.exchange, item.buyAverage)
  },
  {
    field: 'sellAverage',
    headerName: 'Sell Avg',
    width: '100px',
    align: 'right',
    render: (item) => exchangeSpecificStringifiedNumber(item.exchange, item.sellAverage)
  },
  {
    field: 'ltp',
    headerName: 'Price',
    width: '100px',
    align: 'right',
    render: (item) => (
      <Box sx={{ 
        color: getQuantityColor(item.net_quantity),
        fontWeight: 500
      }}>
        {exchangeSpecificStringifiedNumber(item.exchange, item.ltp)}
      </Box>
    )
  },
  {
    field: 'pnl',
    headerName: 'P&L',
    width: '120px',
    align: 'right',
    render: (item) => (
      <Box sx={{ 
        color: getQuantityColor(item.pnl),
        fontWeight: 500
      }}>
        ₹{exchangeSpecificStringifiedNumber(item.exchange, Math.abs(item.pnl))}
      </Box>
    )
  },
  // {
  //   field: 'actions',
  //   headerName: '',
  //   width: '80px',
  //   render: (item) => (
  //     <Box 
  //       sx={{ 
  //         opacity: 0,
  //         transition: 'opacity 0.2s',
  //         '.MuiTableRow-root:hover &': {
  //           opacity: 1
  //         }
  //       }}
  //     >
  //       <Button
  //         variant="outlined"
  //         size="small"
  //         sx={{
  //           minWidth: 'auto',
  //           px: 1,
  //           py: 0.5,
  //           fontSize: '0.75rem'
  //         }}
  //         onClick={(e) => {
  //           e.stopPropagation();
  //           item.onConvert?.(item);
  //         }}
  //       >
  //         Convert
  //       </Button>
  //     </Box>
  //   )
  // }
]; 